import { Card, Space, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { EyebrowLabel } from '@viq-admin/ui';
import type { LeadCompleteness } from '@viq-admin/types';
import { LEAD_RULES_COPY } from './copy';
import { failureLabel, LEAD_FIELD_LABELS } from './labels';

/**
 * Lead detail's company lead rules card (spec 2026-09-14 §3.4). Lists what the
 * lead is still missing, in requirement order, or says it meets every
 * requirement. The payload carries failures only (plan refinement 8); the
 * notes counter beside the Notes heading shows the notes minimum met or not.
 * The caller renders this only for an unsent lead whose company has rules on.
 */
export function RequirementsChecklist({ completeness }: { completeness: LeadCompleteness }) {
  return (
    <Card size="small" data-testid="lead-requirements">
      <EyebrowLabel>{LEAD_RULES_COPY.checklistTitle}</EyebrowLabel>
      {completeness.complete ? (
        <Typography.Text type="success">
          <CheckCircleOutlined /> {LEAD_RULES_COPY.checklistComplete}
        </Typography.Text>
      ) : (
        <Space direction="vertical" size={4}>
          {completeness.failures.map((failure) => (
            <Typography.Text
              key={failure.requirementId}
              type="danger"
              data-testid={`lead-requirement-missing-${failure.requirementId}`}
            >
              <CloseCircleOutlined /> {failureLabel(failure)}
            </Typography.Text>
          ))}
          {completeness.sendBlocked &&
            completeness.notes &&
            completeness.failures.some((f) => f.type === 'notes_min_chars') && (
              <Typography.Text type="secondary">
                {LEAD_RULES_COPY.sendBlockedLine(completeness.notes.need)}
              </Typography.Text>
            )}
          {/* Spec 2026-09-28 §6: the fields the company's rules hold this lead for. */}
          {completeness.held && completeness.held.length > 0 && (
            <Typography.Text type="secondary">
              {`${LEAD_RULES_COPY.heldPrefix} ${completeness.held.map((f) => LEAD_FIELD_LABELS[f]).join(', ')}`}
            </Typography.Text>
          )}
        </Space>
      )}
    </Card>
  );
}
